import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Bell } from 'lucide-react';
import { PRIORITY_OPTIONS } from '../constants';

export const DispositionDialog = ({ 
  open, 
  onOpenChange, 
  lead,
  dispositionForm, 
  setDispositionForm, 
  pipelineStages,
  getStageConfig,
  onSubmit 
}) => {
  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o) {
        onOpenChange({ open: false, lead: null });
        setDispositionForm({ 
          newStatus: '', 
          notes: '', 
          createReminder: false, 
          reminderTitle: '', 
          reminderDueDate: '', 
          reminderType: 'follow_up', 
          reminderPriority: 'medium' 
        });
      }
    }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Lead Status</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Current Status</Label>
            <Badge style={{ backgroundColor: getStageConfig(lead?.status).color + '20', color: getStageConfig(lead?.status).color }}>
              {getStageConfig(lead?.status).label}
            </Badge>
          </div>
          <div className="space-y-2">
            <Label>New Status *</Label>
            <Select value={dispositionForm.newStatus} onValueChange={(v) => setDispositionForm({...dispositionForm, newStatus: v})}>
              <SelectTrigger data-testid="disposition-status-select">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                {pipelineStages.map(s => (
                  <SelectItem key={s.slug} value={s.slug}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                      {s.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea 
              value={dispositionForm.notes}
              onChange={(e) => setDispositionForm({...dispositionForm, notes: e.target.value})}
              placeholder="Add notes about this status change..."
              rows={2}
            />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Create Follow-up Reminder
              </Label>
              <Switch 
                checked={dispositionForm.createReminder}
                onCheckedChange={(checked) => setDispositionForm({...dispositionForm, createReminder: checked})}
                data-testid="create-reminder-switch"
              />
            </div>
            
            {dispositionForm.createReminder && (
              <div className="space-y-3 mt-4 p-3 bg-slate-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Reminder Title *</Label>
                  <Input 
                    value={dispositionForm.reminderTitle}
                    onChange={(e) => setDispositionForm({...dispositionForm, reminderTitle: e.target.value})}
                    placeholder="e.g., Follow up on site visit"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Due Date *</Label>
                    <Input 
                      type="datetime-local"
                      value={dispositionForm.reminderDueDate}
                      onChange={(e) => setDispositionForm({...dispositionForm, reminderDueDate: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={dispositionForm.reminderPriority} onValueChange={(v) => setDispositionForm({...dispositionForm, reminderPriority: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map(p => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange({ open: false, lead: null })}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="update-disposition-btn">Update Status</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
